/* eslint-disable @typescript-eslint/no-explicit-any */
import { SorterResult } from 'antd/es/table/interface'
import { DefineTypeEventEnum } from 'app/api/event/constant'
import { IEvent, TypeEventEnum } from 'app/api/event/model'
import R from 'app/assets/R'
import { ENV_CONFIG } from 'app/common/config'
import { ISort } from 'app/common/interface'
import BaseText from 'app/components/common/BaseText'
import { EVENT_MANAGEMENT_DETAILS_PATH } from 'app/components/router/route-path'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { FORMAT_DD_MM_YYYY_HH_MM } from 'app/constant'
import { useDebounce, usePagination } from 'app/hook'
import { useGetEventList } from 'app/react-query/hook/event'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadExcel } from 'app/service/general/service'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { isEqual } from 'lodash'
import { DateUtil, FONT_WEIGHT, moment, Pagination } from 'parkway-web-common'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { IEventTableData, IFilterEventList } from './type'

const today = new Date()
export const useHook = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [filter, setFilter] = useState<IFilterEventList>({})

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sorts, setSort] = useState<ISort[]>([])

  const filterDebounce = useDebounce(filter, 500)

  const { data: dataEvent, isLoading } = useGetEventList({
    page,
    limit: pageSize,
    sorts,
    startDate: today.toISOString(),
    endDate: moment(today).add('days', 7).toISOString(),
  })

  const { flattenDataList } = usePagination()

  const eventData = useMemo(() => {
    const res: IResponseQueryList<IEvent[]> = flattenDataList(dataEvent)
    return res
  }, [dataEvent])

  const data: IEventTableData[] = useMemo(() => {
    return eventData?.data?.map((item, index) => {
      return {
        ...item,
        key: index + 1,
      }
    })
  }, [eventData])

  const onNavigateDetail = (event: IEvent) => {
    navigate(EVENT_MANAGEMENT_DETAILS_PATH, {
      state: {
        event,
      },
    })
  }

  const columns = useMemo(() => {
    return [
      convertColumnTable<IEventTableData>({
        key: 'name_event',
        title: t(R.strings.event_name),
        classNameWidthColumnOverwrite: 'very-big-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <RenderValueTable
              value={record?.name_event}
              type="Link"
              onClick={() => onNavigateDetail(record)}
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'description',
        title: 'Thông tin sự kiện',
        classNameWidthColumnOverwrite: 'big-column',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={<RenderText text={record?.description ?? ''} />}
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_register_start',
        title: t(R.strings.start_date_register),
        sorter: true,
        render: (_, record) => {
          if (isEqual(record?.type, TypeEventEnum.NEWS)) {
            return '-'
          }
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_register_start
                  ? DateUtil?.formatDate(
                      record?.time_register_start ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_register_end',
        title: t(R.strings.end_date_register),
        sorter: true,
        render: (_, record) => {
          if (isEqual(record?.type, TypeEventEnum.NEWS)) {
            return '-'
          }
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_register_end
                  ? DateUtil?.formatDate(
                      record?.time_register_end ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_start',
        title: t(R.strings.start_date_event),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_start
                  ? DateUtil?.formatDate(
                      record?.time_start ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),

      convertColumnTable<IEventTableData>({
        key: 'time_end',
        title: t(R.strings.end_date_event),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_end
                  ? DateUtil?.formatDate(
                      record?.time_end ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),

      convertColumnTable({
        key: 'type',
        title: 'Loại sự kiện',
        sorter: true,
        render: (type: TypeEventEnum) => {
          return (
            <BaseText
              children={DefineTypeEventEnum[type]?.text ?? '-'}
              fontWeight="semibold"
              colorText="statesGreenColor"
            />
          )
        },
      }),
    ]
  }, [t])

  const onChangeFilter = (newFilter: IFilterEventList) => {
    setFilter(newFilter)
    setPage(1)
  }

  const handleChangeTable = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPage(pagination?.current ?? 1)
    setPageSize(pagination?.pageSize ?? 10)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const downloadExcel = async (params: {
    search?: string
    type?: string[]
    status?: string[]
    startDate?: string
    endDate?: string
  }): Promise<void> => {
    await handleDownloadExcel({
      params: {
        ...params,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}admin/event/download`,
      fileName: 'Sự kiện',
    })
  }

  const handleDownloadEventExcel = async () => {
    await downloadExcel({
      search: filterDebounce?.search,
      status: filterDebounce?.status,
      type: filterDebounce?.type,
      startDate: filterDebounce?.startDateDayJs?.toISOString(),
      endDate: filterDebounce?.endDateDayJs?.toISOString(),
    })
  }

  return {
    isLoading,
    columns,
    data,
    onChangeFilter,
    filter,
    page,
    pageSize,
    total: eventData?.total,
    handleChangeTable,
    handleDownloadEventExcel,
  }
}

export const RenderText: React.FC<{ text: string }> = ({ text }) => {
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g

  return (
    <span>
      {text.split(urlRegex).map((part, index) =>
        urlRegex.test(part) ? (
          <a
            style={{
              color: convertedVariables.collapseBackgroundColor,
              fontWeight: FONT_WEIGHT.regular,
            }}
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  )
}
